import React, { useState, useEffect } from 'react';
import { AttachToTacticCard } from '../Common/ExtraDefenses';
import { isOperator } from '../../utils/permission';
import MonsterSelect from './MonsterSelect';
import { primeKeyboard } from '../../utils/keyboard';
import CounterPost from './CounterPost';
import TacticPost from './TacticPost';
import { CounterListState, DefenseTeam } from '../../hooks/useCounter';
import { getMonsterImgPath } from '../../utils/monster';
import OpponentSearch, { OpponentBadge, OpponentDeck, OpponentDeckPanel, OpponentWizard } from '../OpponentSearch';
import { useCanSeeOpponentRunes } from '../BattleRunes';

import DefenseTeamList from '../../components/DefenseTeamList';
import ProfileWithName from '../../components/ProfileWithName';
import MonsterImage from '../../components/MonsterImage';
import Caret from '../../components/Caret';
import { useBackHandler } from '../../hooks/useBackHandler';
import { guildImg } from '../../utils/guildImage';
import { useDeckMemos, DeckMemoButton, DeckMemoModal } from '../Common/DeckMemo';
import LearnedDecks from '../Common/LearnedDecks';

export interface CounterListProps extends CounterListState {
  monsterMapping: Record<string, any>;
  user: any;
  onPostClick: (post: any) => void;
  onPostDeleted?: () => void;
}

const CounterList: React.FC<CounterListProps> = ({
  monsterMapping,
  user,
  searchSide,
  setSearchSide,
  selectedMonsters,
  setSelectedMonsters,
  counterModalOpen,
  setCounterModalOpen,
  counterFilteredTeams,
  setCounterFilteredTeams,
  selectedTeamIdx,
  setSelectedTeamIdx,
  counterList,
  setCounterList,
  loadingCounter,
  setLoadingCounter,
  handleSearch,
  handleSelectTeam,
  onPostClick,
  onPostDeleted,
}) => {
  const [selectedPost, setSelectedPost] = useState<any>(null);
  /**
   * **상대 이름으로 검색** — 상대 방덱 룬 보기가 켜진 사람에게만 셋째 갈래로 보인다(사용자 지시 2026-10-10).
   * 켜면 몹 고르기 칸 대신 이름 칸이 나오고, 찾은 방덱들은 **방덱 검색과 같은 결과 화면**에 실린다
   * (방덱 목록 → 누르면 공덱별 기록). 그 화면이 기대는 상태(`counterFilteredTeams`·`counterList`)를 그대로 채운다.
   */
  const seeOpp = useCanSeeOpponentRunes();
  const [oppMode, setOppMode] = useState(false);
  const opp = seeOpp && oppMode;
  const [oppDecks, setOppDecks] = useState<OpponentDeck[]>([]);
  /** 찾은 소환사 — 방덱·공덱 목록 맨 위에 이름·길드 엠블럼을 보인다 */
  const [oppWizard, setOppWizard] = useState<OpponentWizard | null>(null);
  const showOppDecks = (w: OpponentWizard | null, decks: OpponentDeck[]) => {
    setOppWizard(w);
    setOppDecks(decks);
    setCounterFilteredTeams(decks.map((d) => ({ team: d.team, source: d.source, counter_count: d.counter_count })));
    setSelectedTeamIdx(null);
    setCounterList(null);
  };
  /** 상대 검색으로 들어가고 나올 때 앞선 결과는 뜻이 달라지므로 비운다 */
  const enterOpp = (on: boolean) => {
    if (on === opp) return;
    setOppMode(on);
    showOppDecks(null, []);
  };
  /** 방덱을 누르면 — 상대 검색이면 받아 둔 공덱별 기록을 바로 싣는다(서버에 다시 묻지 않는다) */
  const selectTeam = (idx: number) => {
    if (!opp) return handleSelectTeam(idx);
    setSelectedTeamIdx(idx);
    setCounterList({ counters: oppDecks[idx]?.counters || [] });
  };
  /** 상대 검색은 방덱(상대)을 보고 공덱(우리)을 늘어놓는다 — 방덱 검색과 같은 쪽 */
  const side = opp ? 'def' : searchSide;

  /**
   * **공략 쓰기 창.**
   *
   * 찾은 방덱에 공략이 하나도 없으면 "없습니다" 로 끝났다 — 아는 사람이 바로 적어 둘 길이
   * 없었다. 이 자리에서 열면 방덱은 이미 정해져 있으니 공덱만 고르면 된다.
   *
   * 공덱으로 찾는 중(`atk`)일 때는 고른 것이 방덱이 아니라 공덱이라 열지 않는다.
   */
  const pickedTeam = selectedTeamIdx !== null ? counterFilteredTeams[selectedTeamIdx] : null;
  /**
   * [공략 작성하러 가기] — 여기서 창을 띄우지 않고 **공략 탭으로 가서** 그 탭의 작성 창을 연다(사용자 지시).
   * 고른 방덱은 그대로 채워 넘긴다. 공략 탭과 탭 전환(PageContainer)이 이 신호를 듣는다.
   */
  const goWriteTactic = () => {
    if (!pickedTeam) return;
    window.dispatchEvent(new CustomEvent('swpvpdb:write-tactic', { detail: { defenseTeam: pickedTeam.team } }));
  };
  const canWrite = !!user && side === 'def' && !!pickedTeam;
  const hasTactic = !!counterList?.counters?.some((c: any) => c.source === 'tactic');
  /**
   * **방덱 × 공덱 메모** (사용자 2026-10-15) — 공덱 줄마다 [메모]. 방덱으로 찾을 때만(공덱으로 찾으면 줄이 방덱이다).
   * 열쇠는 서버가 몹 조합으로 만들고, 화면은 묶음 id 로 줄과 짝짓는다.
   */
  const memoOn = side === 'def' && !!pickedTeam;
  const memoTeams = React.useMemo(() => {
    if (!memoOn || !counterList) return [] as Array<{ key: string; team: any[] }>;
    const seen = new Map<string, any[]>();
    for (const c of counterList.counters as any[]) {
      if (c.source !== 'db') continue;
      const team = c.monsters || c.attackTeam;
      const gid = String(c.groupId || c._id);
      if (!seen.has(gid) && Array.isArray(team) && team.length === 3) seen.set(gid, team);
    }
    return Array.from(seen.entries()).map(([key, team]) => ({ key, team }));
  }, [memoOn, counterList]);
  const { memos: deckMemos, setOne: setDeckMemo } = useDeckMemos(memoOn ? pickedTeam!.team : null, memoTeams);
  const [memoOpen, setMemoOpen] = useState<{ key: string; team: any[] } | null>(null);

  /**
   * 상세로 열 자료를 가져온다.
   *
   * 점령전 기록(`siege-…`)은 게시물이 아니라 `/api/posts/posting` 에 없다. 404 본문을
   * 그대로 상세로 넘기면 attackTeam 없는 객체가 되어 화면이 터졌다 — **전투기록을
   * 클릭하면 오류**가 난 원인이다. 그런 줄은 목록이 이미 들고 있는 값으로 만든다.
   */
  const loadRecordDetail = async (c: any) => {
    const isSiege = typeof c._id === 'string' && c._id.startsWith('siege-');
    if (!isSiege) {
      const base = c.source === 'tactic' ? '/api/tactics' : '/api/posts';
      const res = await fetch(
        c.source === 'tactic' ? `${base}/${c._id}` : `${base}/posting/${c._id}`,
        { credentials: 'include' }
      );
      return await res.json();
    }
    const picked = selectedTeamIdx !== null ? (counterFilteredTeams[selectedTeamIdx]?.team || []) : [];
    const shown = c.attackTeam || c.monsters || [];
    // 공덱으로 검색했으면 줄에 보이는 쪽이 방덱이고, 고른 덱이 공덱이다
    return {
      ...c,
      attackTeam: side === 'atk' ? picked : shown,
      defenseTeam: side === 'atk' ? shown : picked
    };
  };

  const [memberWinRates, setMemberWinRates] = useState<{ [nickname: string]: { wins: number; losses: number; winRate: number } }>({});
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [selectedGroup, setSelectedGroup] = useState<{ key: string; items: any[] } | null>(null);
  const [groupTab, setGroupTab] = useState<'members' | 'records'>('members');
  const [expandedMemberKey, setExpandedMemberKey] = useState<string | null>(null);

  // counterList 변경 시 펼침 상태 초기화
  useEffect(() => {
    if (counterList?.counters) {
      // 새로운 검색 시 모든 그룹 접기
      setExpandedGroups(new Set());
    }
  }, [counterList]);

  // 길드원 승률 정보 가져오기
  useEffect(() => {
    const fetchWinRates = async () => {
      if (!counterList?.counters) return;

      // 고유한 작성자(설탕 길드원) 추출
      const authorMap = new Map<string, { nickname: string; uuid: string }>();
      counterList.counters.forEach((c: any) => {
        if (c.authorUuid && !authorMap.has(c.authorUuid)) {
          authorMap.set(c.authorUuid, { nickname: c.author, uuid: c.authorUuid });
        }
      });

      const uuids = Array.from(authorMap.values()).map(a => a.uuid);

      if (uuids.length === 0) return;

      try {
        const res = await fetch('/api/posts/win-rates', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ uuids })
        });

        if (res.ok) {
          const data = await res.json();
          if (data.success && data.winRates) {
            // uuid를 nickname으로 매핑
            const stats: { [nickname: string]: { wins: number; losses: number; winRate: number } } = {};
            for (const [uuid, winRateData] of Object.entries(data.winRates) as [string, any][]) {
              const author = authorMap.get(uuid);
              if (author) {
                stats[author.nickname] = {
                  wins: winRateData.wins,
                  losses: winRateData.losses,
                  winRate: winRateData.winRate
                };
              }
            }
            setMemberWinRates(stats);
          }
        }
      } catch (error) {
      }
    };

    fetchWinRates();
  }, [counterList]);

  // 뒤로 가기: 글 창 → 그룹 상세 창 순으로 닫는다 (몹 고르기 창은 PageContainer 가 등록)
  useBackHandler(!!selectedGroup, () => setSelectedGroup(null));
  useBackHandler(!!selectedPost, () => setSelectedPost(null));

  return (
    <div style={{
      padding: 24,
      textAlign: 'center',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      minHeight: '100vh',
      position: 'relative',
      overflow: 'hidden'
    }}>

      <div style={{
        position: 'relative',
        zIndex: 2,
        width: '100%',
        maxWidth: 1200,
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '40px 20px'
      }}>
        {/* 헤더 섹션 */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.95)',
          borderRadius: 24,
          padding: '40px 32px 32px',
          marginBottom: 32,
          boxShadow: '0 12px 40px rgba(0, 0, 0, 0.1)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          width: '100%',
          position: 'relative',
          zIndex: 100,
          overflow: 'visible'
        }}>
          <h2 style={{
            margin: 0,
            marginBottom: 8,
            fontWeight: 900,
            fontSize: 40,
            letterSpacing: -1,
            textAlign: 'center',
            background: 'linear-gradient(135deg, #1976d2 0%, #764ba2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>전투기록 검색</h2>
          <p style={{
            margin: 0,
            marginBottom: 32,
            fontSize: 14,
            color: '#888',
            fontWeight: 500,
            textAlign: 'center'
          }}>{opp ? '상대 소환사 이름을 적으면 그 사람의 방덱들이 나옵니다' : '리더몹을 포함한 몬스터를 2마리 이상 선택해주세요'}</p>
          {/* 무엇으로 찾을지 — 방덱 / 공덱 */}
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 20, marginBottom: 24 }}>
            {([['def', '방덱으로 검색'], ['atk', '공덱으로 검색'], ...(seeOpp ? [['opp', '상대 이름으로 검색']] : [])] as Array<['def' | 'atk' | 'opp', string]>).map(([key, label]) => (
              <label key={key} style={{
                display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer',
                fontSize: 14, fontWeight: 700,
                color: (key === 'opp' ? opp : !opp && searchSide === key) ? '#667eea' : '#999'
              }}>
                <input
                  type="radio"
                  name="counter-search-side"
                  checked={key === 'opp' ? opp : !opp && searchSide === key}
                  onChange={() => { if (key === 'opp') enterOpp(true); else { enterOpp(false); setSearchSide(key); } }}
                  style={{ accentColor: '#667eea', width: 16, height: 16, cursor: 'pointer' }}
                />
                {label}
              </label>
            ))}
          </div>
          {opp && (
            <OpponentSearch isMobile={false} setLoading={setLoadingCounter} onLoaded={showOppDecks} />
          )}
          <div style={{ display: opp ? 'none' : 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 24 }}>
            {/* 몬스터 선택 박스 3개 */}
            <div style={{ display: 'flex', gap: 20, marginBottom: 16 }}>
              {[0,1,2].map(idx => (
                <div key={idx} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                  {/* 첫 칸은 리더 자리다. 칸이 어긋나지 않게 나머지 칸에도 같은 라벨을 투명하게 둔다 */}
                  <span style={{
                    fontSize: 10,
                    fontWeight: 800,
                    letterSpacing: 0.5,
                    lineHeight: 1,
                    userSelect: 'none',
                    color: idx === 0 ? '#667eea' : 'transparent'
                  }}>LEADER</span>
                  <div
                  /* 다음에 고를 칸(앞에서부터 첫 빈 칸)이 밝아졌다 어두워졌다 한다 */
                  className={!selectedMonsters[idx] && [0,1,2].findIndex(i => !selectedMonsters[i]) === idx ? 'pick-slot-pulse' : undefined}
                  style={{
                    width: 70,
                    height: 70,
                    background: '#fff',
                    borderRadius: 12,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    border: selectedMonsters[idx] ? '2px solid #667eea' : '2px dashed #ddd',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    transform: 'scale(1)'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.05)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)'; }}
                  onClick={() => { primeKeyboard(); setCounterModalOpen(idx); }}>
                  {selectedMonsters[idx] ? (
                    <>
                      <div style={{ width: 63, height: 63, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', borderRadius: 12, overflow: 'hidden', border: 'none' }}>
                        <MonsterImage imgPath={selectedMonsters[idx].image_path} alt={selectedMonsters[idx].displayName} style={{ width: 65, height: 65, objectFit: 'contain', borderRadius: 12 }} />
                      </div>
                      <button onClick={e => { e.stopPropagation(); setSelectedMonsters(selectedMonsters.map((m, i) => i === idx ? null : m)); }} style={{ position: 'absolute', top: -8, right: -8, background: '#ff6b6b', border: 'none', borderRadius: '50%', width: 24, height: 24, color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer', boxShadow: '0 2px 6px rgba(0,0,0,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.3s ease' }}
                        onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.1)'; e.currentTarget.style.boxShadow = '0 3px 8px rgba(0,0,0,0.3)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.2)'; }}
                      >×</button>
                    </>
                  ) : (
                    <span style={{ fontSize: 32, color: '#ccc', userSelect: 'none', fontWeight: 300 }}>+</span>
                  )}
                  </div>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 12, marginBottom: 0 }}>
              <button
                onClick={handleSearch}
                style={{
                  background: selectedMonsters[0] && selectedMonsters[1] ? '#667eea' : '#ccc',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 12,
                  padding: '14px 40px',
                  fontWeight: 700,
                  fontSize: 16,
                  cursor: selectedMonsters[0] && selectedMonsters[1] ? 'pointer' : 'not-allowed',
                  transition: 'all 0.3s ease',
                  transform: 'scale(1)',
                  boxShadow: selectedMonsters[0] && selectedMonsters[1] ? '0 2px 8px rgba(102, 126, 234, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.1)'
                }}
                onMouseEnter={(e) => {
                  if (selectedMonsters[0] && selectedMonsters[1]) {
                    e.currentTarget.style.transform = 'scale(1.05)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  if (selectedMonsters[0] && selectedMonsters[1]) {
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(102, 126, 234, 0.3)';
                  }
                }}
                disabled={!(selectedMonsters[0] && selectedMonsters[1])}
              >
                검색하기
              </button>
            </div>
          </div>
        </div>

        {/* 컨텐츠 섹션 */}
        {counterFilteredTeams && counterFilteredTeams.length > 0 && (
        <div style={{
          background: 'rgba(255, 255, 255, 0.95)',
          borderRadius: 24,
          padding: 32,
          boxShadow: '0 12px 40px rgba(0, 0, 0, 0.1)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          width: '100%',
          minHeight: 400,
          position: 'relative',
          zIndex: 1
        }}>
        {/* 반응형: 900px 이하에서는 세로 배치 */}
        <div style={{
          display: 'flex', flexDirection: window.innerWidth < 900 ? 'column' : 'row', justifyContent: 'center', alignItems: 'flex-start', width: '100%', minHeight: 480, gap: 32, maxWidth: 1100, margin: '0 auto', boxSizing: 'border-box'
        }}>
          {/* 왼쪽: 방덱 목록 */}
          {counterFilteredTeams && counterFilteredTeams.length > 0 && (
          <div style={{
            width: window.innerWidth < 900 ? '100%' : '50%',
            minWidth: 280,
            maxWidth: 480,
            background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.98) 0%, rgba(245, 247, 255, 0.98) 100%)',
            borderRadius: 16,
            boxShadow: '0 10px 40px rgba(102, 126, 234, 0.12), 0 2px 8px rgba(0, 0, 0, 0.06), inset 0 1px 2px rgba(255, 255, 255, 0.8)',
            padding: window.innerWidth < 900 ? 16 : 20,
            marginRight: window.innerWidth < 900 ? 0 : 8,
            marginBottom: window.innerWidth < 900 ? 24 : 0,
            boxSizing: 'border-box',
            overflow: 'visible',
            border: '2px solid transparent',
            backgroundImage: 'linear-gradient(145deg, rgba(255, 255, 255, 0.98) 0%, rgba(245, 247, 255, 0.98) 100%), linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
            backgroundOrigin: 'border-box',
            backgroundClip: 'padding-box, border-box',
            position: 'relative',
            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
          }}>
            <div style={{ position: 'relative', zIndex: 1 }}>
              {opp && oppWizard && <OpponentBadge wizard={oppWizard} isMobile={false} />}
              <DefenseTeamList defenseTeams={counterFilteredTeams} monsterMapping={monsterMapping} onSelect={selectTeam} selectedIdx={selectedTeamIdx} />
            </div>
          </div>
          )}
          {/* 오른쪽: 카운터 리스트 */}
          {(selectedTeamIdx !== null && counterFilteredTeams[selectedTeamIdx] && counterList) && (
          <div style={{
            width: window.innerWidth < 900 ? '100%' : '45%',
            minWidth: 280,
            maxWidth: 480,
            background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.98) 0%, rgba(245, 247, 255, 0.98) 100%)',
            borderRadius: 16,
            boxShadow: '0 10px 40px rgba(102, 126, 234, 0.12), 0 2px 8px rgba(0, 0, 0, 0.06), inset 0 1px 2px rgba(255, 255, 255, 0.8)',
            padding: window.innerWidth < 900 ? 16 : 20,
            marginLeft: window.innerWidth < 900 ? 0 : 8,
            boxSizing: 'border-box',
            overflow: 'visible',
            minHeight: 320,
            border: '2px solid transparent',
            backgroundImage: 'linear-gradient(145deg, rgba(255, 255, 255, 0.98) 0%, rgba(245, 247, 255, 0.98) 100%), linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
            backgroundOrigin: 'border-box',
            backgroundClip: 'padding-box, border-box',
            position: 'relative',
            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
          }}>
            {selectedTeamIdx !== null && counterFilteredTeams[selectedTeamIdx] ? (
              <div style={{ position: 'relative', zIndex: 1 }}>
                {/*
                  **무슨 방덱을 보고 있나.** 목록만 있으면 몇 번 스크롤한 뒤에는 어느 덱을
                  누른 것인지 잊는다 — 방덱 목록으로 돌아가 다시 확인해야 했다.
                */}
                {/* 상대 이름 검색 — 고른 상대 방덱을 크게, 가장 최근 룬까지 */}
                {opp && oppWizard && <OpponentBadge wizard={oppWizard} isMobile={false} />}
                {opp && selectedTeamIdx !== null && oppDecks[selectedTeamIdx] && (
                  <OpponentDeckPanel deck={oppDecks[selectedTeamIdx]} monsterMapping={monsterMapping} isMobile={false} />
                )}
                {!opp && pickedTeam && (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '8px 12px', marginBottom: 10,
                    // 왼쪽 굵은 띠 — 공덱 파랑·방덱 빨강(사용자 2026-10-27 초안 1)
                    borderRadius: 0, borderLeft: `4px solid ${side === 'atk' ? '#1976d2' : '#d32f2f'}`,
                    background: side === 'atk' ? 'rgba(25, 118, 210, 0.08)' : 'rgba(211, 47, 47, 0.07)'
                  }}>
                    {/* 공덱으로 찾았으면 고른 덱은 공덱이다(사용자 2026-10-27) */}
                    <span style={{ fontSize: 13, fontWeight: 800, color: side === 'atk' ? '#1976d2' : '#d32f2f', flexShrink: 0 }}>{side === 'atk' ? '공덱' : '방덱'}</span>
                    <div style={{ display: 'flex', gap: 4 }}>
                      {pickedTeam.team.map((m, idx) => {
                        const { imgPath, displayName } = getMonsterImgPath(monsterMapping, m);
                        return imgPath ? (
                          <img loading="lazy"
                            key={idx}
                            src={`/mobs/v2/${imgPath.replace(/\\/g, '/')}`}
                            alt={displayName}
                            title={displayName}
                            style={{
                              width: 40, height: 40, borderRadius: 8, objectFit: 'contain',
                              background: '#fff', border: '1.5px solid rgba(102, 126, 234, 0.2)'
                            }}
                          />
                        ) : (
                          <div key={idx} style={{
                            width: 40, height: 40, borderRadius: 8, background: '#f5f5f5',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: '#999', fontSize: 8, border: '1.5px solid rgba(102, 126, 234, 0.15)'
                          }}>{displayName}</div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {loadingCounter ? (
                  <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: 400,
                    padding: 32
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      color: '#667eea',
                      fontSize: 16,
                      fontWeight: 600
                    }}>
                      <div style={{
                        width: 32,
                        height: 32,
                        border: '4px solid rgba(102, 126, 234, 0.2)',
                        borderTopColor: '#667eea',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }} />
                      <span>카운터 덱을 불러오는 중...</span>
                      <style>{`
                        @keyframes spin {
                          to { transform: rotate(360deg); }
                        }
                      `}</style>
                    </div>
                  </div>
                ) : counterList ? (
                  <>
                    {/*
                      **공략이 없을 때 그 자리에 쓰러 가는 줄을 둔다.**
                      공략이 있으면 이 목록 맨 위에 금색 배지와 함께 떴다. 없으면 아무것도 안
                      떠서, 여기 무엇이 빠졌는지도 몰랐다. 같은 자리에 같은 크기로 둔다.
                    */}
                    {!hasTactic && canWrite && (
                      <div
                        onClick={goWriteTactic}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 12,
                          padding: '12px 14px', marginBottom: 12,
                          borderRadius: 12, cursor: 'pointer',
                          border: '2px dashed rgba(102, 126, 234, 0.45)',
                          background: 'rgba(102, 126, 234, 0.05)',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(102, 126, 234, 0.12)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(102, 126, 234, 0.05)'; }}
                      >
                        <div style={{
                          background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                          color: '#fff', fontSize: 10, fontWeight: 800, padding: '4px 8px',
                          borderRadius: 6, whiteSpace: 'nowrap', letterSpacing: 0.5,
                          boxShadow: '0 2px 6px rgba(255, 193, 7, 0.4)'
                        }}>공략</div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 14, fontWeight: 700, color: '#667eea' }}>공략 작성하러 가기</div>
                          <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>이 방덱에 적힌 공략이 아직 없습니다</div>
                        </div>
                        <div style={{ fontSize: 20, color: '#667eea', fontWeight: 700, flexShrink: 0 }}>+</div>
                      </div>
                    )}
                    {/* 새로 쓰지 않고 이 방덱을 기존 공략의 호환 방덱으로 — 운영진만(사용자) */}
                    {!hasTactic && canWrite && pickedTeam && isOperator(user) && (
                      <AttachToTacticCard defenseTeam={pickedTeam.team} mapping={monsterMapping} isMobile={false}
                        onDone={() => { if (selectedTeamIdx !== null) selectTeam(selectedTeamIdx); }} />
                    )}
                    {/* 시뮬 권한이 있으면 이 방덱의 학습 결과 공덱 (사용자 2026-10-29) */}
                    <LearnedDecks user={user} team={side === 'def' && pickedTeam ? pickedTeam.team : null} monsterMapping={monsterMapping} isMobile={false} />

                    {counterList.counters.length > 0 && (() => {
                      // 정렬된 카운터 목록
                      const sortedCounters = [...counterList.counters].sort((a, b) => {
                        // 승률이 높은 길드원이 먼저 (공성 수로 줄 세우던 것은 걷어냈다)
                        const aWinRate = memberWinRates[a.author]?.winRate || 0;
                        const bWinRate = memberWinRates[b.author]?.winRate || 0;
                        return bWinRate - aWinRate;
                      });

                      // 공략과 전투기록 분리
                      const tactics = sortedCounters.filter((c: any) => c.source === 'tactic');
                      const posts = sortedCounters.filter((c: any) => c.source === 'db');

                      // 전투기록만 백엔드에서 제공하는 groupId로 그룹화
                      const groupMap = new Map<string, any[]>();

                      posts.forEach((counter: any) => {
                        const gid = counter.groupId || counter._id;
                        if (!groupMap.has(gid)) {
                          groupMap.set(gid, []);
                        }
                        groupMap.get(gid)!.push(counter);
                      });

                      // Map을 배열로 변환
                      const groups = Array.from(groupMap.entries()).map(([key, items]) => ({ key, items }));

                      // 그룹 정렬: 1. 전투기록 개수 많은 순, 2. 승률 높은 순
                      groups.sort((a, b) => {
                        // 1. 전투기록 개수로 정렬 (많은 순)
                        if (b.items.length !== a.items.length) {
                          return b.items.length - a.items.length;
                        }
                        // 2. 같은 개수면 승률로 정렬
                        const aWinRate = memberWinRates[a.items[0]?.author]?.winRate || 0;
                        const bWinRate = memberWinRates[b.items[0]?.author]?.winRate || 0;
                        return bWinRate - aWinRate;
                      });

                      return (
                        <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 12 }}>
                          {/* 공략 먼저 표시 */}
                          {tactics.map((tactic: any, tacticIdx: number) => (
                            <React.Fragment key={tactic._id || `tactic-${tacticIdx}`}>
                              <li
                                key={`tactic-item-${tactic._id || tacticIdx}`}
                                style={{
                                  background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.15) 0%, rgba(255, 165, 0, 0.1) 100%)',
                                  borderRadius: 12,
                                  boxShadow: '0 3px 12px rgba(255, 193, 7, 0.3)',
                                  padding: '14px 16px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 12,
                                  border: '2px solid rgba(255, 193, 7, 0.5)',
                                  minWidth: 0,
                                  boxSizing: 'border-box',
                                  cursor: 'pointer',
                                  transition: 'all 0.3s ease',
                                  transform: 'scale(1)',
                                  position: 'relative',
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.transform = 'translateY(-2px)';
                                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(255, 193, 7, 0.4)';
                                  e.currentTarget.style.borderColor = 'rgba(255, 193, 7, 0.7)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.transform = 'translateY(0)';
                                  e.currentTarget.style.boxShadow = '0 3px 12px rgba(255, 193, 7, 0.3)';
                                  e.currentTarget.style.borderColor = 'rgba(255, 193, 7, 0.5)';
                                }}
                                onClick={async () => {
                                  if (tactic._id) {
                                    try {
                                      let newViews = tactic.views || 0;
                                      const apiBase = '/api/tactics';
                                      try {
                                        const viewRes = await fetch(`${apiBase}/view`, {
                                          method: 'POST',
                                          headers: { 'Content-Type': 'application/json' },
                                          body: JSON.stringify({ id: tactic._id })
                                        });
                                        const viewData = await viewRes.json();
                                        if (viewData && typeof viewData.views === 'number') {
                                          newViews = viewData.views;
                                        }
                                      } catch {}
                                      const res = await fetch(`${apiBase}/${tactic._id}`, { credentials: 'include' });
                                      const data = await res.json();
                                      setSelectedPost({
                                        ...data,
                                        nickname: tactic.author,
                                        profileImg: tactic.profileImg,
                                        isDeleted: tactic.isDeleted,
                                        views: newViews,
                                        source: tactic.source,
                                      });
                                    } catch {}
                                  }
                                }}
                              >
                                {/* 공략 배지 */}
                                <div style={{
                                  background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                                  color: '#fff',
                                  fontSize: 10,
                                  fontWeight: 800,
                                  padding: '4px 8px',
                                  borderRadius: 6,
                                  boxShadow: '0 2px 6px rgba(255, 193, 7, 0.4)',
                                  whiteSpace: 'nowrap',
                                  letterSpacing: 0.5
                                }}>
                                  공략
                                </div>
                                {/* 몬스터 이미지 */}
                                <div style={{ display: 'flex', gap: 4, minWidth: 100, flexShrink: 0 }}>
                                  {(tactic.monsters || tactic.attackTeam)?.map((m: any, idx: number) => {
                                    const { imgPath, displayName } = getMonsterImgPath(monsterMapping, m);
                                    return imgPath ? (
                                      <img loading="lazy" key={idx} src={`/mobs/v2/${imgPath.replace(/\\/g, '/')}`} alt={displayName} style={{ width: 36, height: 36, borderRadius: 8, objectFit: 'contain', background: 'linear-gradient(135deg, #fff 0%, #f8faff 100%)', border: '1.5px solid rgba(102, 126, 234, 0.15)', boxShadow: '0 2px 6px rgba(102, 126, 234, 0.08)' }} />
                                    ) : (
                                      <div key={idx} style={{ width: 36, height: 36, borderRadius: 8, background: 'linear-gradient(135deg, #f5f5f5 0%, #f0f0f5 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999', fontSize: 8, border: '1.5px solid rgba(102, 126, 234, 0.15)' }}>{displayName}</div>
                                    );
                                  })}
                                </div>
                                {/* 프로필+닉네임 */}
                                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'center', gap: 4 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <img loading="lazy"
                                      src={
                                        tactic.profileImg || '/images/default-profile.png'
                                      }
                                      alt="프로필"
                                      style={{ width: 24, height: 24, borderRadius: 6, objectFit: 'cover', background: '#fff', border: '1.5px solid rgba(102, 126, 234, 0.2)', boxShadow: '0 2px 4px rgba(102, 126, 234, 0.1)' }}
                                      onError={e => { (e.target as HTMLImageElement).src = '/images/default-profile.png'; }}
                                    />
                                    <span style={{ color: tactic.isDeleted ? '#ff6b6b' : '#333', fontWeight: 600, fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{tactic.author}</span>
                                  </div>
                                  {tactic.date && (
                                    <span style={{ fontSize: 11, color: '#999' }}>{tactic.date}</span>
                                  )}
                                </div>
                              </li>
                            </React.Fragment>
                          ))}

                          {/* 전투기록 그룹 */}
                          {groups.map((group, groupIdx) => (
                            <React.Fragment key={group.key}>
                              {/* 항목이 2개 이상이면 축약 카드 표시 (항상) */}
                              {group.items.length > 1 && (
                                <React.Fragment>
                                  <li
                                    style={{
                                      background: 'rgba(255, 255, 255, 0.9)',
                                      borderRadius: 12,
                                      boxShadow: '0 2px 8px rgba(102, 126, 234, 0.08)',
                                      padding: '12px 16px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 12,
                                      border: '1px solid rgba(102, 126, 234, 0.1)',
                                      minWidth: 0,
                                      boxSizing: 'border-box',
                                      cursor: 'pointer',
                                      transition: 'all 0.3s ease',
                                    }}
                                    onClick={() => {
                                      setSelectedGroup(group);
                                      setGroupTab('members');
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.transform = 'translateY(-2px)';
                                      e.currentTarget.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.15)';
                                      e.currentTarget.style.borderColor = 'rgba(102, 126, 234, 0.3)';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.transform = 'translateY(0)';
                                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(102, 126, 234, 0.08)';
                                      e.currentTarget.style.borderColor = 'rgba(102, 126, 234, 0.1)';
                                    }}
                                  >
                                    {/* 몬스터 이미지 */}
                                    <div style={{ display: 'flex', gap: 4, minWidth: 100, flexShrink: 0 }}>
                                      {(group.items[0].monsters || group.items[0].attackTeam)?.map((m: any, idx: number) => {
                                        const { imgPath, displayName } = getMonsterImgPath(monsterMapping, m);
                                        return imgPath ? (
                                          <img loading="lazy" key={idx} src={`/mobs/v2/${imgPath.replace(/\\/g, '/')}`} alt={displayName} style={{ width: 36, height: 36, borderRadius: 8, objectFit: 'contain', background: 'linear-gradient(135deg, #fff 0%, #f8faff 100%)', border: '1.5px solid rgba(102, 126, 234, 0.15)', boxShadow: '0 2px 6px rgba(102, 126, 234, 0.08)' }} />
                                        ) : (
                                          <div key={idx} style={{ width: 36, height: 36, borderRadius: 8, background: 'linear-gradient(135deg, #f5f5f5 0%, #f0f0f5 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999', fontSize: 8, border: '1.5px solid rgba(102, 126, 234, 0.15)' }}>{displayName}</div>
                                        );
                                      })}
                                    </div>
                                    {memoOn && (
                                      <DeckMemoButton memo={deckMemos[String(group.key)]}
                                        onClick={() => setMemoOpen({ key: String(group.key), team: group.items[0].monsters || group.items[0].attackTeam })} />
                                    )}
                                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, justifyContent: 'center' }}>
                                      <div style={{ fontSize: 12, color: '#999', fontWeight: 600, letterSpacing: -0.3 }}>
                                        전투 {group.items[0].totalMatches || group.items.length}회
                                      </div>
                                      {(() => {
                                        const wins = group.items[0].wins || 0;
                                        const losses = group.items[0].losses || 0;
                                        const winRate = group.items[0].winRate?.toFixed(1) || '0.0';

                                        return (
                                          <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 10,
                                            background: 'rgba(0, 0, 0, 0.02)',
                                            padding: '5px 14px',
                                            borderRadius: 14,
                                            border: '1px solid rgba(0, 0, 0, 0.06)'
                                          }}>
                                            <span style={{ fontSize: 12, color: '#5a7a9b', fontWeight: 700 }}>{wins}승</span>
                                            <div style={{ width: 1, height: 12, background: 'rgba(0, 0, 0, 0.1)' }}></div>
                                            <span style={{ fontSize: 12, color: '#9b5a6f', fontWeight: 700 }}>{losses}패</span>
                                            <div style={{ width: 1, height: 12, background: 'rgba(0, 0, 0, 0.1)' }}></div>
                                            <span style={{ fontSize: 12, color: '#8b7355', fontWeight: 700 }}>{winRate}%</span>
                                          </div>
                                        );
                                      })()}
                                    </div>
                                    <div style={{ fontSize: 14, color: '#667eea', fontWeight: 700 }}>
                                      <Caret />
                                    </div>
                                  </li>
                                </React.Fragment>
                              )}

                              {/* 항목이 1개면 바로 표시 */}
                              {group.items.length === 1 && group.items.map((c, itemIdx) => {
                                const isWin = c.result === 'win';
                                const isLose = c.result === 'lose';
                                return (
                                    <React.Fragment key={c._id || `${group.key}-${itemIdx}`}>
                          <li
                            key={`item-${c._id || `${group.key}-${itemIdx}`}`}
                            style={{
                              background: c.source === 'tactic' ? 'linear-gradient(135deg, rgba(255, 215, 0, 0.15) 0%, rgba(255, 165, 0, 0.1) 100%)' : isLose ? 'rgba(255, 235, 238, 0.9)' : isWin ? 'rgba(227, 242, 253, 0.9)' : 'rgba(255, 255, 255, 0.9)',
                              borderRadius: 12,
                              boxShadow: c.source === 'tactic' ? '0 3px 12px rgba(255, 193, 7, 0.3)' : isLose ? '0 2px 8px rgba(244, 67, 54, 0.12)' : isWin ? '0 2px 8px rgba(33, 150, 243, 0.12)' : '0 2px 8px rgba(102, 126, 234, 0.08)',
                              padding: '14px 16px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 12,
                              border: c.source === 'tactic' ? '2px solid rgba(255, 193, 7, 0.5)' : isLose ? '1px solid rgba(244, 67, 54, 0.25)' : isWin ? '1px solid rgba(33, 150, 243, 0.25)' : '1px solid rgba(102, 126, 234, 0.1)',
                              minWidth: 0,
                              boxSizing: 'border-box',
                              cursor: 'pointer',
                              transition: 'all 0.3s ease',
                              transform: 'scale(1)',
                              position: 'relative',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'translateY(-2px)';
                              if (c.source === 'tactic') {
                                e.currentTarget.style.boxShadow = '0 8px 24px rgba(255, 193, 7, 0.4)';
                                e.currentTarget.style.borderColor = 'rgba(255, 193, 7, 0.7)';
                              } else {
                                e.currentTarget.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.15)';
                                e.currentTarget.style.borderColor = 'rgba(102, 126, 234, 0.3)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'translateY(0)';
                              if (c.source === 'tactic') {
                                e.currentTarget.style.boxShadow = '0 3px 12px rgba(255, 193, 7, 0.3)';
                                e.currentTarget.style.borderColor = 'rgba(255, 193, 7, 0.5)';
                              } else {
                                e.currentTarget.style.boxShadow = '0 2px 8px rgba(102, 126, 234, 0.08)';
                                e.currentTarget.style.borderColor = 'rgba(102, 126, 234, 0.1)';
                              }
                            }}
                            onClick={async () => {
                              if (c._id) {
                                try {
                                  // 조회수 증가
                                  let newViews = c.views || 0;
                                  const apiBase = c.source === 'tactic' ? '/api/tactics' : '/api/posts';
                                  try {
                                    const viewRes = await fetch(`${apiBase}/view`, {
                                      method: 'POST',
                                      headers: { 'Content-Type': 'application/json' },
                                      body: JSON.stringify({ id: c._id })
                                    });
                                    const viewData = await viewRes.json();
                                    if (viewData && typeof viewData.views === 'number') {
                                      newViews = viewData.views;
                                    }
                                  } catch {}
                                  // 게시글 상세 fetch
                                  const data = await loadRecordDetail(c);
                                  setSelectedPost({
                                    ...data,
                                    nickname: c.author,
                                    profileImg: c.profileImg,
                                    isDeleted: c.isDeleted,
                                    views: newViews,
                                    source: c.source,
                                  });
                                } catch {}
                              }
                            }}
                          >
                            {/* 공략 배지 */}
                            {c.source === 'tactic' && (
                              <div style={{
                                background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                                color: '#fff',
                                fontSize: 10,
                                fontWeight: 800,
                                padding: '4px 8px',
                                borderRadius: 6,
                                boxShadow: '0 2px 6px rgba(255, 193, 7, 0.4)',
                                whiteSpace: 'nowrap',
                                letterSpacing: 0.5
                              }}>
                                공략
                              </div>
                            )}
                            {/* 몬스터 이미지 */}
                            <div style={{ display: 'flex', gap: 4, minWidth: 100, flexShrink: 0 }}>
                              {(c.monsters || c.attackTeam)?.map((m: any, idx: number) => {
                                const mon = c.monsters ? m : m;
                                const { imgPath, displayName } = getMonsterImgPath(monsterMapping, mon);
                                return imgPath ? (
                                  <img loading="lazy" key={idx} src={`/mobs/v2/${imgPath.replace(/\\/g, '/')}`} alt={displayName} style={{ width: 36, height: 36, borderRadius: 8, objectFit: 'contain', background: 'linear-gradient(135deg, #fff 0%, #f8faff 100%)', border: '1.5px solid rgba(102, 126, 234, 0.15)', boxShadow: '0 2px 6px rgba(102, 126, 234, 0.08)' }} />
                                ) : (
                                  <div key={idx} style={{ width: 36, height: 36, borderRadius: 8, background: 'linear-gradient(135deg, #f5f5f5 0%, #f0f0f5 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999', fontSize: 8, border: '1.5px solid rgba(102, 126, 234, 0.15)' }}>{displayName}</div>
                                );
                              })}
                            </div>
                            {memoOn && c.source === 'db' && (
                              <DeckMemoButton memo={deckMemos[String(group.key)]}
                                onClick={() => setMemoOpen({ key: String(group.key), team: c.monsters || c.attackTeam })} />
                            )}
                            {/* 프로필+닉네임 */}
                            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'center', gap: 4 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <img loading="lazy"
                                  src={
                                    c.profileImg || '/images/default-profile.png'
                                  }
                                  alt="프로필"
                                  style={{ width: 24, height: 24, borderRadius: 6, objectFit: 'cover', background: '#fff', border: '1.5px solid rgba(102, 126, 234, 0.2)', boxShadow: '0 2px 4px rgba(102, 126, 234, 0.1)' }}
                                  onError={e => { (e.target as HTMLImageElement).src = '/images/default-profile.png'; }}
                                />
                                <span style={{ color: c.isDeleted ? '#ff6b6b' : '#333', fontWeight: 600, fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.author}</span>
                              </div>
                              {c.date && (
                                <span style={{ fontSize: 11, color: '#999' }}>{c.date}</span>
                              )}
                            </div>
                          </li>
                                    </React.Fragment>
                                );
                              })}

                            </React.Fragment>
                          ))}
                        </ul>
                      );
                    })()}

                    {/*
                      **공략이 없을 때.** 예전에는 이 칸이 아예 안 떠서 "없다" 는 것조차
                      화면에 안 남았다. 아는 사람이 바로 적어 둘 수 있게 단추를 둔다.
                    */}
                    {counterList.counters.length === 0 && (
                      <div style={{ padding: '36px 16px', textAlign: 'center' }}>
                        <div style={{ color: '#999', fontSize: 15, fontWeight: 600, marginBottom: 14 }}>
                          아직 이 방덱을 뚫은 기록도, 공략도 없습니다.
                        </div>
                        {canWrite ? (
<>
                          <button
                        onClick={goWriteTactic}
                        style={{
                          border: 'none',
                          borderRadius: 10,
                          padding: '12px 20px',
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          color: '#fff',
                          fontSize: 15,
                          fontWeight: 700,
                          cursor: 'pointer',
                          boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
                        }}
                      >공략 작성하러 가기</button>
                          {pickedTeam && isOperator(user) && (
                            <div><AttachToTacticCard compact defenseTeam={pickedTeam.team} mapping={monsterMapping} isMobile={false}
                              onDone={() => { if (selectedTeamIdx !== null) selectTeam(selectedTeamIdx); }} /></div>
                          )}
</>
                        ) : (
                          <div style={{ color: '#bbb', fontSize: 13 }}>로그인하면 공략을 쓸 수 있습니다.</div>
                        )}
                      </div>
                    )}

                  </>
                ) : (
                  <div style={{ color: '#999', fontSize: 16, padding: 32, textAlign: 'center', fontWeight: 500 }}>카운터 정보를 불러올 수 없습니다.</div>
                )}
              </div>
            ) : (
              <div style={{ color: '#999', padding: 48, textAlign: 'center', fontSize: 16, fontWeight: 500 }}>{side === 'atk' ? '공덱을 클릭하면 상대한 방덱이 표시됩니다.' : '방덱을 클릭하면 카운터가 표시됩니다.'}</div>
            )}
          </div>
          )}
        </div>
        </div>
        )}
      </div>

      {/* 로딩 인디케이터: 검색 중이거나 카운터 찾는 중일 때 화면 중앙에 표시 */}
      {(loadingCounter || (selectedMonsters.length > 0 && counterFilteredTeams === null)) && (
        <div style={{
          position: 'fixed',
          left: 0, top: 0, width: '100vw', height: '100vh',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 9999, background: 'rgba(0,0,0,0.3)'
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 16,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            padding: '32px 48px',
            fontSize: 18,
            fontWeight: 600,
            color: '#667eea',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
            border: '1px solid #e0e0e0'
          }}>
            <div style={{
              width: 32,
              height: 32,
              border: '4px solid rgba(102, 126, 234, 0.2)',
              borderTopColor: '#667eea',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
            <span>전투기록 검색 중...</span>
            <style>{`
              @keyframes spin {
                to { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        </div>
      )}

      {/* 몬스터 선택 모달 */}
      {typeof counterModalOpen === 'number' && (
        <MonsterSelect
          autoFocus
          open={true}
          monsterMapping={monsterMapping}
          onClose={() => setCounterModalOpen(null)}
          onSelect={mob => {
            const newArr = [...selectedMonsters];
            newArr[counterModalOpen] = mob;
            setSelectedMonsters(newArr.filter(Boolean));
            setCounterModalOpen(null);
          }}
        />
      )}

      {/* 그룹 상세 모달 */}
      {/*
        그룹 창(길드원·전투기록 탭). 기록을 눌러 상세 창을 띄우면 예전엔 이 창을 닫아 버려서, 상세 창을 닫고 나면 펼쳐 둔
        길드원·탭·내린 자리가 다 사라졌다(사용자 2026-10-20). 이제 닫지 않고 **상세 창이 떠 있는 동안만 숨긴다** —
        이 창이 상세 창보다 위 층(zIndex)이라 그냥 두면 상세 창을 가린다. 뒤로 가기도 상세 창 → 그룹 창 순으로 한 칸씩 닫힌다.
      */}
      {selectedGroup && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0, 0, 0, 0.5)',
            display: selectedPost ? 'none' : 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000
          }}
          onClick={() => setSelectedGroup(null)}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 20,
              width: '90%',
              maxWidth: 600,
              maxHeight: '80vh',
              overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              display: 'flex',
              flexDirection: 'column'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* 헤더 */}
            <div style={{
              padding: '20px 24px',
              borderBottom: '1px solid #eee',
              display: 'flex',
              alignItems: 'center',
              gap: 16
            }}>
              {/* 몬스터 이미지 */}
              <div style={{ display: 'flex', gap: 6 }}>
                {(selectedGroup.items[0].monsters || selectedGroup.items[0].attackTeam)?.map((m: any, idx: number) => {
                  const { imgPath, displayName } = getMonsterImgPath(monsterMapping, m);
                  return imgPath ? (
                    <img loading="lazy" key={idx} src={`/mobs/v2/${imgPath.replace(/\\/g, '/')}`} alt={displayName} style={{ width: 44, height: 44, borderRadius: 10, objectFit: 'contain', background: '#f8faff', border: '2px solid rgba(102, 126, 234, 0.15)' }} />
                  ) : (
                    <div key={idx} style={{ width: 44, height: 44, borderRadius: 10, background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999', fontSize: 9 }}>{displayName}</div>
                  );
                })}
              </div>
              {/* 통계 */}
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, color: '#666', fontWeight: 600, marginBottom: 4, textAlign: 'left' }}>
                  전투 {selectedGroup.items[0].totalMatches || selectedGroup.items.length}회
                </div>
                <div style={{ display: 'flex', gap: 12 }}>
                  <span style={{ fontSize: 14, color: '#5a7a9b', fontWeight: 700 }}>{selectedGroup.items[0].wins || 0}승</span>
                  <span style={{ fontSize: 14, color: '#9b5a6f', fontWeight: 700 }}>{selectedGroup.items[0].losses || 0}패</span>
                  <span style={{ fontSize: 14, color: '#8b7355', fontWeight: 700 }}>{selectedGroup.items[0].winRate?.toFixed(1) || '0.0'}%</span>
                </div>
              </div>
              {/* 닫기 버튼 */}
              <button
                onClick={() => setSelectedGroup(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: 24,
                  color: '#999',
                  cursor: 'pointer',
                  padding: 8
                }}
              >×</button>
            </div>

            {/* 탭 */}
            <div style={{
              display: 'flex',
              borderBottom: '1px solid #eee'
            }}>
              <button
                onClick={() => setGroupTab('members')}
                data-subtab="" style={{
                  flex: 1,
                  padding: '14px 0',
                  border: 'none',
                  background: groupTab === 'members' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#f5f5f5',
                  color: groupTab === 'members' ? '#fff' : '#666',
                  fontWeight: 700,
                  fontSize: 14,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
              >
                길드원
              </button>
              <button
                onClick={() => setGroupTab('records')}
                data-subtab="" style={{
                  flex: 1,
                  padding: '14px 0',
                  border: 'none',
                  background: groupTab === 'records' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#f5f5f5',
                  color: groupTab === 'records' ? '#fff' : '#666',
                  fontWeight: 700,
                  fontSize: 14,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
              >
                전투기록
              </button>
            </div>

            {/* 콘텐츠 */}
            <div style={{
              flex: 1,
              overflow: 'auto',
              padding: 20
            }}>
              {groupTab === 'members' ? (
                // 길드원 탭
                <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {(() => {
                    // 길드원별 승/패 집계
                    const memberStats = new Map<string, { nickname: string; profileImg: string; wins: number; losses: number; records: any[]; key: string }>();
                    selectedGroup.items.forEach((c: any) => {
                      const key = c.authorUuid || c.author;
                      if (!memberStats.has(key)) {
                        memberStats.set(key, { nickname: c.author, profileImg: c.profileImg, wins: 0, losses: 0, records: [], key });
                      }
                      const stat = memberStats.get(key)!;
                      if (c.result === 'win') stat.wins++;
                      else if (c.result === 'lose') stat.losses++;
                      stat.records.push(c);
                    });

                    // 정렬: 승률 높은 순, 같으면 전투 수 많은 순
                    const sortedMembers = Array.from(memberStats.values()).sort((a, b) => {
                      const aWinRate = a.wins + a.losses > 0 ? a.wins / (a.wins + a.losses) : 0;
                      const bWinRate = b.wins + b.losses > 0 ? b.wins / (b.wins + b.losses) : 0;
                      if (bWinRate !== aWinRate) return bWinRate - aWinRate;
                      return (b.wins + b.losses) - (a.wins + a.losses);
                    });

                    return sortedMembers.map((member, idx) => {
                      const total = member.wins + member.losses;
                      const winRate = total > 0 ? ((member.wins / total) * 100).toFixed(1) : '0.0';
                      const isExpanded = expandedMemberKey === member.key;
                      return (
                        <li
                          key={idx}
                          style={{
                            background: 'rgba(255, 255, 255, 0.9)',
                            borderRadius: 12,
                            border: isExpanded ? '1px solid rgba(102, 126, 234, 0.3)' : '1px solid rgba(102, 126, 234, 0.15)',
                            boxShadow: isExpanded ? '0 4px 16px rgba(102, 126, 234, 0.12)' : '0 2px 8px rgba(102, 126, 234, 0.06)',
                            overflow: 'hidden',
                            transition: 'all 0.3s ease'
                          }}
                        >
                          {/* 길드원 헤더 (클릭 가능) */}
                          <div
                            style={{
                              padding: '14px 16px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 14,
                              cursor: 'pointer',
                              transition: 'background 0.2s ease'
                            }}
                            onClick={() => setExpandedMemberKey(isExpanded ? null : member.key)}
                            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(102, 126, 234, 0.04)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                          >
                            {/* 프로필 */}
                            <img loading="lazy"
                              src={
                                member.profileImg || '/images/default-profile.png'
                              }
                              alt="프로필"
                              style={{ width: 40, height: 40, borderRadius: 10, objectFit: 'cover', border: '2px solid rgba(102, 126, 234, 0.2)' }}
                              onError={e => { (e.target as HTMLImageElement).src = '/images/default-profile.png'; }}
                            />
                            {/* 닉네임 */}
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 700, fontSize: 15, color: '#333', marginBottom: 4 }}>{member.nickname}</div>
                              <div style={{ fontSize: 12, color: '#888' }}>전투 {total}회</div>
                            </div>
                            {/* 승패 통계 */}
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 10,
                              background: 'rgba(0, 0, 0, 0.03)',
                              padding: '8px 14px',
                              borderRadius: 10
                            }}>
                              <span style={{ fontSize: 14, color: '#5a7a9b', fontWeight: 700, lineHeight: '1' }}>{member.wins}승</span>
                              <div style={{ width: 1, height: 14, background: 'rgba(0, 0, 0, 0.1)' }}></div>
                              <span style={{ fontSize: 14, color: '#9b5a6f', fontWeight: 700, lineHeight: '1' }}>{member.losses}패</span>
                              <div style={{ width: 1, height: 14, background: 'rgba(0, 0, 0, 0.1)' }}></div>
                              <span style={{ fontSize: 14, color: '#8b7355', fontWeight: 700, lineHeight: '1' }}>{winRate}%</span>
                            </div>
                            {/* 펼침 아이콘 */}
                            <div style={{
                              fontSize: 14,
                              color: '#667eea',
                              fontWeight: 700,
                              transition: 'transform 0.3s ease',
                              transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)'
                            }}>
                              <Caret />
                            </div>
                          </div>

                          {/* 전투기록 드롭다운 */}
                          {isExpanded && (
                            <div style={{
                              borderTop: '1px solid rgba(102, 126, 234, 0.1)',
                              background: 'rgba(102, 126, 234, 0.02)',
                              padding: '12px 16px',
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 8
                            }}>
                              {member.records.map((c: any, recordIdx: number) => {
                                const isWin = c.result === 'win';
                                const isLose = c.result === 'lose';
                                return (
                                  <div
                                    key={c._id || recordIdx}
                                    style={{
                                      background: isLose ? 'rgba(255, 235, 238, 0.9)' : isWin ? 'rgba(227, 242, 253, 0.9)' : 'rgba(255, 255, 255, 0.9)',
                                      borderRadius: 10,
                                      padding: '10px 14px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 10,
                                      border: isLose ? '1px solid rgba(244, 67, 54, 0.25)' : isWin ? '1px solid rgba(33, 150, 243, 0.25)' : '1px solid rgba(102, 126, 234, 0.15)',
                                      cursor: 'pointer',
                                      transition: 'all 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.transform = 'translateX(4px)';
                                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(102, 126, 234, 0.15)';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.transform = 'translateX(0)';
                                      e.currentTarget.style.boxShadow = 'none';
                                    }}
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      if (c._id) {
                                        try {
                                          let newViews = c.views || 0;
                                          const apiBase = '/api/posts';
                                          try {
                                            const viewRes = await fetch(`${apiBase}/view`, {
                                              method: 'POST',
                                              headers: { 'Content-Type': 'application/json' },
                                              body: JSON.stringify({ id: c._id })
                                            });
                                            const viewData = await viewRes.json();
                                            if (viewData && typeof viewData.views === 'number') {
                                              newViews = viewData.views;
                                            }
                                          } catch {}
                                          const data = await loadRecordDetail(c);
                                          setSelectedPost({
                                            ...data,
                                            nickname: c.author,
                                            profileImg: c.profileImg,
                                            isDeleted: c.isDeleted,
                                            views: newViews,
                                            source: c.source,
                                          });
                                          // 그룹 창은 닫지 않는다 — 상세 창이 떠 있는 동안 숨겨 두고, 닫으면 펼쳐 둔 그대로 다시 보인다
                                        } catch {}
                                      }
                                    }}
                                  >
                                    {/* 결과 배지 */}
                                    <div style={{
                                      background: isWin ? 'linear-gradient(135deg, #2196F3 0%, #1976D2 100%)' : isLose ? 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)' : '#999',
                                      color: '#fff',
                                      fontSize: 11,
                                      fontWeight: 700,
                                      padding: '4px 10px',
                                      borderRadius: 6,
                                      minWidth: 30,
                                      textAlign: 'center'
                                    }}>
                                      {isWin ? '승' : isLose ? '패' : '-'}
                                    </div>
                                    {/* 구분선 */}
                                    <div style={{ width: 1, height: 16, background: 'rgba(0, 0, 0, 0.1)', flexShrink: 0 }} />
                                    {/* 닉네임 VS 상대길드 */}
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, minWidth: 0 }}>
                                      <img loading="lazy"
                                        src={
                                          c.profileImg || '/images/default-profile.png'
                                        }
                                        alt="프로필"
                                        style={{ width: 22, height: 22, borderRadius: 5, objectFit: 'cover', border: '1px solid rgba(102, 126, 234, 0.2)', flexShrink: 0 }}
                                        onError={e => { (e.target as HTMLImageElement).src = '/images/default-profile.png'; }}
                                      />
                                      <span style={{ fontWeight: 600, fontSize: 12, color: '#333', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 80 }}>{c.author}</span>
                                      <span style={{ color: '#999', fontSize: 12, fontWeight: 600, flexShrink: 0 }}>VS</span>
                                      {c.siegeWar && (
                                        <img loading="lazy"
                                          src={guildImg(c.siegeWarImg || c.siegeWar)}
                                          alt={c.siegeWar}
                                          style={{ width: 22, height: 22, borderRadius: 5, objectFit: 'cover', border: '1px solid rgba(102, 126, 234, 0.2)', flexShrink: 0 }}
                                          onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                        />
                                      )}
                                      <span style={{ fontWeight: 600, fontSize: 12, color: '#667eea', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.siegeWar || '알수없음'}</span>
                                    </div>
                                    {/* 구분선 */}
                                    <div style={{ width: 1, height: 16, background: 'rgba(0, 0, 0, 0.1)', flexShrink: 0 }} />
                                    {/* 날짜 */}
                                    <div style={{ fontSize: 12, color: '#999', flexShrink: 0 }}>
                                      {c.date || (c.createdAt && new Date(c.createdAt).toLocaleDateString('ko-KR', { month: '2-digit', day: '2-digit' }).replace(/\. /g, '/').replace('.', ''))}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </li>
                      );
                    });
                  })()}
                </ul>
              ) : (
                // 전투기록 탭
                <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {selectedGroup.items.map((c: any, idx: number) => {
                    const isWin = c.result === 'win';
                    const isLose = c.result === 'lose';
                    return (
                      <li
                        key={c._id || idx}
                        style={{
                          background: isLose ? 'rgba(255, 235, 238, 0.9)' : isWin ? 'rgba(227, 242, 253, 0.9)' : 'rgba(255, 255, 255, 0.9)',
                          borderRadius: 12,
                          padding: '10px 12px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          border: isLose ? '1px solid rgba(244, 67, 54, 0.25)' : isWin ? '1px solid rgba(33, 150, 243, 0.25)' : '1px solid rgba(102, 126, 234, 0.15)',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 6px 16px rgba(102, 126, 234, 0.12)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                        onClick={async () => {
                          if (c._id) {
                            try {
                              let newViews = c.views || 0;
                              const apiBase = '/api/posts';
                              try {
                                const viewRes = await fetch(`${apiBase}/view`, {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ id: c._id })
                                });
                                const viewData = await viewRes.json();
                                if (viewData && typeof viewData.views === 'number') {
                                  newViews = viewData.views;
                                }
                              } catch {}
                              const data = await loadRecordDetail(c);
                              setSelectedPost({
                                ...data,
                                nickname: c.author,
                                profileImg: c.profileImg,
                                isDeleted: c.isDeleted,
                                views: newViews,
                                source: c.source,
                              });
                              // 그룹 창은 닫지 않는다 — 상세 창이 떠 있는 동안 숨겨 두고, 닫으면 펼쳐 둔 그대로 다시 보인다
                            } catch {}
                          }
                        }}
                      >
                        {/* 결과 배지 */}
                        <div style={{
                          background: isWin ? 'linear-gradient(135deg, #2196F3 0%, #1976D2 100%)' : isLose ? 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)' : '#999',
                          color: '#fff',
                          fontSize: 11,
                          fontWeight: 700,
                          padding: '4px 10px',
                          borderRadius: 6,
                          minWidth: 30,
                          textAlign: 'center',
                          flexShrink: 0
                        }}>
                          {isWin ? '승' : isLose ? '패' : '-'}
                        </div>
                        {/* 구분선 */}
                        <div style={{ width: 1, height: 16, background: 'rgba(0, 0, 0, 0.1)', flexShrink: 0 }} />
                        {/* 프로필+닉네임 */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, width: 100, flexShrink: 0 }}>
                          <img loading="lazy"
                            src={
                              c.profileImg || '/images/default-profile.png'
                            }
                            alt="프로필"
                            style={{ width: 22, height: 22, borderRadius: 5, objectFit: 'cover', border: '1px solid rgba(102, 126, 234, 0.2)', flexShrink: 0 }}
                            onError={e => { (e.target as HTMLImageElement).src = '/images/default-profile.png'; }}
                          />
                          <span style={{ fontWeight: 600, fontSize: 12, color: c.isDeleted ? '#ff6b6b' : '#333', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.author}</span>
                        </div>
                        {/* 구분선 */}
                        <div style={{ width: 1, height: 16, background: 'rgba(0, 0, 0, 0.1)', flexShrink: 0 }} />
                        {/* 공덱 VS 방덱 */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, flexShrink: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            {(c.attackTeam || c.monsters)?.map((m: any, mIdx: number) => {
                              const { imgPath, displayName } = getMonsterImgPath(monsterMapping, m);
                              return imgPath ? (
                                <img loading="lazy" key={mIdx} src={`/mobs/v2/${imgPath.replace(/\\/g, '/')}`} alt={displayName} style={{ width: 24, height: 24, borderRadius: 5, objectFit: 'contain', background: '#f8faff', border: '1px solid rgba(102,126,234,0.15)' }} />
                              ) : (
                                <div key={mIdx} style={{ width: 24, height: 24, borderRadius: 5, background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 7, color: '#999' }}>{displayName}</div>
                              );
                            })}
                          </div>
                          <span style={{ fontSize: 10, fontWeight: 600, color: '#667eea', flexShrink: 0 }}>VS</span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            {(selectedTeamIdx !== null ? counterFilteredTeams[selectedTeamIdx]?.team : null)?.map((m: any, mIdx: number) => {
                              const { imgPath, displayName } = getMonsterImgPath(monsterMapping, m);
                              return imgPath ? (
                                <img loading="lazy" key={mIdx} src={`/mobs/v2/${imgPath.replace(/\\/g, '/')}`} alt={displayName} style={{ width: 24, height: 24, borderRadius: 5, objectFit: 'contain', background: '#f8faff', border: '1px solid rgba(102,126,234,0.15)' }} />
                              ) : (
                                <div key={mIdx} style={{ width: 24, height: 24, borderRadius: 5, background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 7, color: '#999' }}>{displayName}</div>
                              );
                            })}
                          </div>
                        </div>
                        {/* 구분선 */}
                        <div style={{ width: 1, height: 16, background: 'rgba(0, 0, 0, 0.1)', flexShrink: 0 }} />
                        {/* 날짜 */}
                        <span style={{ fontSize: 12, color: '#999', flexShrink: 0, marginLeft: 'auto', textAlign: 'right' }}>
                          {c.date || (c.createdAt && new Date(c.createdAt).toLocaleDateString('ko-KR', { month: '2-digit', day: '2-digit' }).replace(/\. /g, '/').replace('.', ''))}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 게시글 팝업 */}
      {selectedPost?.source === 'tactic' ? (
        <TacticPost
          open={!!selectedPost}
          onClose={() => setSelectedPost(null)}
          tactic={selectedPost}
          monsterMapping={monsterMapping}
          user={user}
          onTacticDeleted={onPostDeleted}
        />
      ) : (
        <CounterPost
          open={!!selectedPost}
          onClose={() => setSelectedPost(null)}
          post={selectedPost}
          monsterMapping={monsterMapping}
          user={user}
          onPostDeleted={onPostDeleted}
        />
      )}
      {/* 공략 쓰기 — 방덱은 이미 정해져 있다 */}
      {memoOpen && pickedTeam && (
        <DeckMemoModal defenseTeam={pickedTeam.team} attackTeam={memoOpen.team} memo={deckMemos[memoOpen.key]}
          canEdit={!!user} mapping={monsterMapping} onClose={() => setMemoOpen(null)}
          onSaved={(m) => setDeckMemo(memoOpen.key, m)} />
      )}

    </div>
  );
};

export default CounterList; 